import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays, Loader2, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";

function apiErrorMessage(err, fallback = "Request failed") {
  return (
    err?.response?.data?.errors?.two_factor_code?.[0]
    || err?.response?.data?.errors?.business_date?.[0]
    || err?.response?.data?.message
    || err?.message
    || fallback
  );
}

const GATE_Z = "2147483646";

function suggestedOpenDate(businessDay) {
  if (businessDay?.suggested_open_date) return businessDay.suggested_open_date;
  if (businessDay?.last_closed_date) {
    const d = new Date(`${businessDay.last_closed_date}T12:00:00`);
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  }
  return businessDay?.system_date || "";
}

/**
 * Mandatory Day Open blocker for Tax Invoice / POS.
 * Authenticator code is required only when this user has enabled 2FA in General Settings.
 */
export default function DayOpenGate({
  businessDay,
  busy = false,
  onOpenDay,
  onLeave,
}) {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const [openDate, setOpenDate] = useState(() => suggestedOpenDate(businessDay));
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { data: twoFaStatus, isLoading: twoFaLoading } = useQuery({
    queryKey: ["user-2fa"],
    queryFn: async () => {
      const { data: res } = await api.get("/user/2fa");
      return res;
    },
    staleTime: 30_000,
  });

  const twoFaEnabled = Boolean(twoFaStatus?.enabled);

  useEffect(() => {
    setOpenDate(suggestedOpenDate(businessDay));
  }, [businessDay]);

  useEffect(() => {
    const enforce = () => {
      const el = rootRef.current;
      if (!el || !el.isConnected) return;
      el.removeAttribute("hidden");
      el.style.setProperty("display", "flex", "important");
      el.style.setProperty("visibility", "visible", "important");
      el.style.setProperty("opacity", "1", "important");
      el.style.setProperty("pointer-events", "auto", "important");
      el.style.setProperty("position", "fixed", "important");
      el.style.setProperty("inset", "0", "important");
      el.style.setProperty("z-index", GATE_Z, "important");
      el.style.setProperty("background", "rgba(15, 23, 42, 0.92)", "important");
      el.style.setProperty("align-items", "center", "important");
      el.style.setProperty("justify-content", "center", "important");
      el.style.setProperty("padding", "1rem", "important");
    };

    enforce();
    const el = rootRef.current;
    if (!el) return undefined;

    const mo = new MutationObserver(() => enforce());
    mo.observe(el, {
      attributes: true,
      attributeFilter: ["style", "class", "hidden"],
    });
    const tick = window.setInterval(enforce, 400);

    const blockKeys = (ev) => {
      if (ev.key === "Escape") {
        ev.preventDefault();
        ev.stopPropagation();
      }
    };
    window.addEventListener("keydown", blockKeys, true);

    return () => {
      mo.disconnect();
      window.clearInterval(tick);
      window.removeEventListener("keydown", blockKeys, true);
    };
  }, []);

  if (typeof document === "undefined") return null;

  const otpOk = !twoFaEnabled || otp.replace(/\s/g, "").length >= 6;
  const canSubmit = Boolean(openDate) && !twoFaLoading && otpOk;
  const isBusy = busy || submitting;

  const handleOpen = async () => {
    setError("");
    setSubmitting(true);
    try {
      await onOpenDay(openDate, twoFaEnabled ? otp.replace(/\s/g, "") : "");
    } catch (err) {
      setError(apiErrorMessage(err, "Day Open failed"));
      setOtp("");
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      ref={rootRef}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="day-open-gate-title"
      aria-describedby="day-open-gate-desc"
      data-day-open-gate="required"
      onContextMenu={(e) => e.preventDefault()}
      style={{
        display: "flex",
        visibility: "visible",
        opacity: 1,
        pointerEvents: "auto",
        position: "fixed",
        inset: 0,
        zIndex: Number(GATE_Z),
        background: "rgba(15, 23, 42, 0.92)",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "28rem",
          borderRadius: "0.75rem",
          background: "#fff",
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.45)",
          padding: "1.5rem",
          pointerEvents: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center gap-2 text-slate-900">
          <CalendarDays className="h-5 w-5 text-rose-600" />
          <h2 id="day-open-gate-title" className="text-lg font-semibold tracking-tight">
            Day Open required
          </h2>
        </div>
        <p id="day-open-gate-desc" className="mb-4 text-sm text-slate-600">
          POS stays locked until a business day is opened.
          {twoFaEnabled
            ? " Enter your Google Authenticator code to continue."
            : " Open with the business date — authenticator is only needed if you enable 2FA in settings."}
        </p>
        {businessDay?.last_closed_date && (
          <p className="mb-3 text-xs text-slate-500">
            Last closed: <strong>{businessDay.last_closed_date}</strong>
            {businessDay?.suggested_open_date ? (
              <>
                {" "}
                · Suggested: <strong>{businessDay.suggested_open_date}</strong>
              </>
            ) : null}
          </p>
        )}
        <div className="space-y-2">
          <Label htmlFor="day-open-gate-date">Business date</Label>
          <Input
            id="day-open-gate-date"
            type="date"
            value={openDate}
            onChange={(e) => setOpenDate(e.target.value)}
            autoFocus={!twoFaEnabled}
          />
        </div>
        {businessDay?.system_date &&
          openDate &&
          openDate !== businessDay.system_date &&
          openDate !== (businessDay.suggested_open_date || "") && (
          <p className="mt-2 text-sm text-amber-700">
            Selected date differs from system date ({businessDay.system_date}).
          </p>
        )}

        {twoFaLoading ? (
          <p className="mt-4 text-[12px] text-slate-500 flex items-center gap-1.5">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking 2FA…
          </p>
        ) : twoFaEnabled ? (
          <div className="mt-4 rounded-lg border border-indigo-100 bg-indigo-50/60 p-3 space-y-2">
            <div className="flex items-center gap-1.5 text-[12px] font-semibold text-indigo-900">
              <Shield className="h-3.5 w-3.5" />
              Authenticator code
            </div>
            <Label htmlFor="day-open-gate-otp">6-digit code from Google Authenticator</Label>
            <Input
              id="day-open-gate-otp"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              value={otp}
              onChange={(e) => {
                setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
                if (error) setError("");
              }}
              placeholder="000000"
              className={`h-9 font-mono tracking-widest ${error ? "border-rose-400 focus-visible:ring-rose-400" : ""}`}
              data-testid="day-open-otp"
              onKeyDown={(e) => {
                if (e.key === "Enter" && canSubmit && !isBusy) {
                  e.preventDefault();
                  handleOpen();
                }
              }}
            />
            {error ? (
              <p
                className="text-[12.5px] font-medium text-rose-700"
                role="alert"
                data-testid="day-open-error"
              >
                {error}
              </p>
            ) : null}
            <button
              type="button"
              className="text-[11px] text-indigo-600 hover:underline"
              onClick={() => navigate("/admin/general")}
            >
              Manage 2FA in General Settings
            </button>
          </div>
        ) : (
          <p className="mt-3 text-[11px] text-slate-500">
            Optional: enable Google Authenticator under{" "}
            <button
              type="button"
              className="text-indigo-600 hover:underline"
              onClick={() => navigate("/admin/general")}
            >
              Admin → General → Two-Factor
            </button>{" "}
            to require a code for Day Open.
          </p>
        )}

        {error && !twoFaEnabled ? (
          <p
            className="mt-3 text-[12.5px] font-medium text-rose-700"
            role="alert"
            data-testid="day-open-error"
          >
            {error}
          </p>
        ) : null}

        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          {typeof onLeave === "function" && (
            <Button type="button" variant="outline" disabled={isBusy} onClick={onLeave}>
              Leave POS
            </Button>
          )}
          <Button
            type="button"
            disabled={isBusy || !canSubmit}
            onClick={handleOpen}
            data-testid="day-open-submit"
          >
            {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Open day"}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
